import { z } from "zod";
import { Nutrition, published } from "@/domains/cats/Nutrition";

export enum FoodType {
  Dry = "Dry",
  Wet = "Wet",
}

/**
 * ペットフードの目的別分類（ペットフード公正競争規約に基づく）
 * - Complete: 総合栄養食。主食として単独で栄養基準を満たす
 * - Complementary: 一般食・おやつ。総合栄養食と併用して補助的に与える
 */
export enum FoodPurpose {
  Complete = "complete",
  Complementary = "complementary",
}

export const FoodIdSchema = z.number().int().brand<"FoodId">();
export type FoodId = z.infer<typeof FoodIdSchema>;

export function foodIdOf(value: number): FoodId {
  return FoodIdSchema.parse(value);
}

const OptionalStringSchema = z.preprocess(
  (value) => value ?? undefined,
  z.string().optional(),
);

const PublishedNutrientSchema = z
  .union([
    z.number(),
    z.object({
      percent: z.number(),
      qualifier: z.enum(["min", "max"]).optional(),
    }),
  ])
  .transform((value) =>
    typeof value === "number"
      ? published(value)
      : published(value.percent, value.qualifier),
  );

const OptionalPublishedNutrientSchema = z.preprocess(
  (value) => value ?? undefined,
  PublishedNutrientSchema.optional(),
);

const PublishedNutrientsSchema = z.object({
  protein: PublishedNutrientSchema,
  fat: PublishedNutrientSchema,
  fiber: PublishedNutrientSchema,
  ash: OptionalPublishedNutrientSchema,
  carbohydrate: OptionalPublishedNutrientSchema,
  totalDietaryFiber: OptionalPublishedNutrientSchema,
});

const AsFedNutritionSchema = PublishedNutrientsSchema.extend({
  basis: z.literal("asFed"),
  moisture: OptionalPublishedNutrientSchema,
});

const DryMatterNutritionSchema = PublishedNutrientsSchema.extend({
  basis: z.literal("dryMatter"),
  moisture: z.never().optional(),
});

const BASIS_REQUIRED_MESSAGE = [
  "水分が未掲載の栄養値には basis を指定してください。",
  "炭水化物を掲載した製品全体基準の表と乾物量分析値の表は同じ形になるため数値からは判別できず、基準を誤ると水分の推定値や他フードとの成分比較が誤ります。",
  "公式表に「水分除去後」「乾物量」の記載があれば dryMatter、製品全体に占める割合として掲載されていれば asFed です。",
].join("");

function fallbackToAsFedWhenMoistureIsPublished(value: unknown): unknown {
  if (typeof value !== "object" || value === null) {
    return value;
  }
  const record = value as Record<string, unknown>;
  const hasMoisture = record.moisture !== undefined && record.moisture !== null;
  return record.basis === undefined && hasMoisture
    ? { ...record, basis: "asFed" }
    : value;
}

const NutritionSchema = z
  .preprocess(
    fallbackToAsFedWhenMoistureIsPublished,
    z.discriminatedUnion(
      "basis",
      [AsFedNutritionSchema, DryMatterNutritionSchema],
      { error: BASIS_REQUIRED_MESSAGE },
    ),
  )
  .transform((source) => new Nutrition(source));

const FoodPurposeSchema = z.preprocess(
  (value) => value ?? FoodPurpose.Complete,
  z.enum(FoodPurpose),
);

const FoodBaseSchema = z.object({
  id: FoodIdSchema,
  purpose: FoodPurposeSchema,
  brand: z.string(),
  series: OptionalStringSchema,
  target: OptionalStringSchema,
  flavor: OptionalStringSchema,
  url: z.string(),
  nutrition: NutritionSchema,
  amazon: z.preprocess(
    (value) => value ?? undefined,
    z
      .object({
        url: z.string(),
        imageUrl: z.string(),
        asin: OptionalStringSchema,
      })
      .optional(),
  ),
  rakuten: z.preprocess(
    (value) => value ?? undefined,
    z
      .object({
        url: z.string(),
        imageUrl: z.string(),
        itemCode: OptionalStringSchema,
      })
      .optional(),
  ),
});

export const DryFoodSchema = FoodBaseSchema.extend({
  type: z.literal(FoodType.Dry),
  kcalPer100: z.number(),
});

export const WetFoodSchema = FoodBaseSchema.extend({
  type: z.literal(FoodType.Wet),
  kcalPerBag: z.number(),
  gramsPerBag: z.number(),
  texture: OptionalStringSchema,
});

export const AllFoodSchema = z.discriminatedUnion("type", [
  DryFoodSchema,
  WetFoodSchema,
]);

export type DryFood = z.infer<typeof DryFoodSchema>;
export type WetFood = z.infer<typeof WetFoodSchema>;
export type AllFood = z.infer<typeof AllFoodSchema>;

export function isComplementaryFood(food: AllFood): boolean {
  return food.purpose === FoodPurpose.Complementary;
}

/** 個包装の袋・パウチ単位で与えるフードかどうか。1袋未満に分けて与えられないため常に1袋分で計算する */
export function isBaggedFood(food: AllFood): food is WetFood {
  return food.type === FoodType.Wet && food.gramsPerBag > 0;
}

export function caloriesPerGram(food: AllFood): number {
  if (food.type === FoodType.Wet) {
    return food.gramsPerBag > 0 ? food.kcalPerBag / food.gramsPerBag : 0;
  }
  return food.kcalPer100 / 100;
}

export function caloriesOf(food: AllFood, grams: number): number {
  return grams * caloriesPerGram(food);
}

export function gramsForCalories(food: AllFood, calories: number): number {
  const perGram = caloriesPerGram(food);
  return perGram > 0 ? calories / perGram : 0;
}

export function sumOfCalories(
  targets: Record<FoodId, { gram: number }>,
  foods: (DryFood | WetFood)[],
): number {
  return Object.entries(targets).reduce((sum, [foodId, value]) => {
    const f = foods.find((x) => x.id === Number(foodId));
    return f === undefined ? sum : sum + caloriesOf(f, value.gram);
  }, 0);
}

export const Shop = { Amazon: "amazon", Rakuten: "rakuten" } as const;
export type Shop = (typeof Shop)[keyof typeof Shop];

export interface PurchaseLink {
  shop: Shop;
  url: string;
  imageUrl: string;
}

export function purchaseLinksOf(food: AllFood): PurchaseLink[] {
  const links: PurchaseLink[] = [];
  if (food.amazon) {
    links.push({
      shop: Shop.Amazon,
      url: food.amazon.url,
      imageUrl: food.amazon.imageUrl,
    });
  }
  if (food.rakuten) {
    links.push({
      shop: Shop.Rakuten,
      url: food.rakuten.url,
      imageUrl: food.rakuten.imageUrl,
    });
  }
  return links;
}

// 商品画像は楽天ウェブサービスで取得したものだけを使う。Amazon の画像は API を介さず取得した URL のため掲載しない
export function productImageOf(food: AllFood): PurchaseLink | undefined {
  return purchaseLinksOf(food).find((link) => link.shop === Shop.Rakuten);
}

export function getFoodDisplayName(food: DryFood | WetFood): string {
  const parts: string[] = [food.brand];

  if (food.series) {
    parts.push(food.series);
  }

  if (food.target) {
    parts.push(`(${food.target})`);
  }

  let displayName = parts.join(" ");

  if (food.flavor) {
    displayName += ` - ${food.flavor}`;
  }

  return displayName;
}
